/**
 * Regeln der Standardordner (Dokumentenklassen) der Akte – Kundenfeedback
 * 16.10.2026 Teil 2 (Nr. VIII, XXXVIII) und 18.10.2026 (Nr. IV, V; Kanzlei
 * Polz, Kundentabelle grün = Standard, rot = nicht standardmäßig).
 *
 * Die Ordnerdefinitionen (Kategorien, Seite, Unterordner) stehen in
 * docClassDefs.ts; hier liegen die reinen Regeln, damit sie ohne Vue testbar
 * sind. Spiegel von backend/internal/fall/form/doc_class_catalog.go.
 *
 *   - standard: erscheint in jeder Akte automatisch
 *   - bedingt:  automatisch, wenn die Bedingung der Akte erfüllt ist
 *               (Bank / Leasing: finanziertes/geleastes Fahrzeug; Autohaus /
 *               Werkstatt: Werkstatt beteiligt bzw. Reparatur dort) – sonst
 *               Vorlage
 *   - vorlage:  nur als Vorlage beim Anlegen eines Ordners
 *   - bestand:  weder Standard noch Vorlage; nur sichtbar, wenn die Akte
 *               schon Dokumente darin hat („E-Mails & Korrespondenz")
 *
 * Unterordner (parentId) folgen ihrem Standardordner.
 */

export type ClassScope = 'alle' | 'haftpflicht' | 'kasko'
export type ClassMode = 'standard' | 'bedingt' | 'vorlage' | 'bestand'

/** Elternordner der Unterordner (Spiegel von parentId in docClassDefs.ts). */
export const CLASS_PARENTS: Readonly<Record<string, string>> = {
  rechnungen: 'schadenbelege',
  versicherung_haftpflicht: 'versicherung',
  versicherung_kasko: 'versicherung',
  versicherung_regulierungsbeauftragter: 'versicherung',
  versicherung_gap: 'versicherung',
  versicherung_auslandsschadenschutz: 'versicherung',
  gericht_instanz1: 'recht',
  gericht_instanz2: 'recht',
  schriftsatz_eigen: 'recht',
  schriftsatz_gegner: 'recht',
  schriftsatz_streitverkuendeter: 'recht',
  gericht_verfuegung: 'recht',
}

/** Automatisch angelegter Grundbestand (Kundentabelle „grün"). */
export const AUTO_CLASS_IDS: ReadonlySet<string> = new Set([
  'vollmacht', // Kunde
  'gutachten', // Gutachter / KVA
  'fahrzeugschaden', // Unterlagen Fahrzeugschaden gesamt
  'schadenbelege', // Schadenbelege (mit Unterordner Rechnungen)
  'versicherung', // Versicherungen (mit Unterordnern)
  'sonstiges', // Sonstiges
])

/** Automatisch nur, wenn die Bedingung der Akte erfüllt ist (sonst Vorlage). */
export const CONDITIONAL_CLASS_IDS: ReadonlySet<string> = new Set(['bank_leasing', 'reparatur'])

/** Weder Standard noch Vorlage – nur Bestandsakten mit Dokumenten (Nr. IV). */
export const LEGACY_ONLY_CLASS_IDS: ReadonlySet<string> = new Set(['korrespondenz'])

/** Wann ein bedingter Ordner automatisch erscheint (Anzeige im Dialog). */
export const CONDITION_HINTS: Readonly<Record<string, string>> = {
  bank_leasing: 'Wird automatisch angelegt, wenn das Fahrzeug geleast oder finanziert ist.',
  reparatur: 'Wird automatisch angelegt, wenn ein Autohaus / eine Werkstatt beteiligt ist oder dort repariert wird.',
}

/**
 * Falltyp-Bezug der vordefinierten Vorlagen. Nicht aufgeführt = „alle".
 * Polizei/Staatsanwaltschaft und Personenschaden betreffen Haftpflichtfälle.
 */
export const CLASS_SCOPES: Readonly<Record<string, ClassScope>> = {
  polizei: 'haftpflicht',
  medizin: 'haftpflicht',
}

/** Erklärung je Standardordner (Nr. XXXVIII) – nach der Ordnertabelle des Kunden. */
export const CLASS_DESCRIPTIONS: Readonly<Record<string, string>> = {
  vollmacht: 'Vollmacht, Mandatsvertrag, Abtretung und die Fragebogen-Versionen des Kunden – nur Kanzlei und Kunde.',
  gutachten: 'Gutachten, Nachträge und Stellungnahmen des Sachverständigen, Kostenvoranschlag, DAT-Kalkulation und die Gutachterrechnung – Kanzlei, Kunde, Werkstatt und Sachverständiger.',
  fahrzeugschaden: 'Sammelansicht über alles zum Fahrzeugschaden: Gutachten, KVA, DAT-Kalkulation, Rechnungen, Prüfbericht und Schadenfotos. Eigene Ablage für Schadenfotos des Fahrzeugs – Kanzlei, Kunde, Werkstatt und Sachverständiger.',
  polizei: 'Polizeibericht, Unfallanzeige, Ermittlungsakte und Schreiben der Staatsanwaltschaft – nur Kanzlei und Kunde.',
  versicherung: 'Schriftwechsel mit den Versicherungen, Deckungszusagen, Abrechnungs- und Nachforderungsschreiben; Unterordner für gegnerische Haftpflicht, Vollkasko, Regulierungsbeauftragten, GAP und Auslandsschadenschutz – nur Kanzlei und Kunde (Prüfbericht auch Werkstatt und Sachverständiger).',
  versicherung_haftpflicht: 'Schriftwechsel mit der gegnerischen Haftpflichtversicherung – nur Kanzlei und Kunde.',
  versicherung_kasko: 'Schriftwechsel mit der Vollkaskoversicherung des Kunden – nur Kanzlei und Kunde.',
  versicherung_regulierungsbeauftragter: 'Schriftwechsel mit dem Regulierungsbeauftragten – nur Kanzlei und Kunde.',
  versicherung_gap: 'Unterlagen zur GAP-Versicherung – nur Kanzlei und Kunde.',
  versicherung_auslandsschadenschutz: 'Unterlagen zum Auslandsschadenschutz – nur Kanzlei und Kunde.',
  recht: 'Gerichtliche Unterlagen mit Unterordnern für I. und II. Instanz, eigene und gegnerische Schriftsätze, Streitverkündete sowie Verfügungen, Termine und Hinweise des Gerichts – nur Kanzlei und Kunde.',
  gericht_instanz1: 'Unterlagen des Verfahrens in der I. Instanz – nur Kanzlei und Kunde.',
  gericht_instanz2: 'Unterlagen des Verfahrens in der II. Instanz – nur Kanzlei und Kunde.',
  schriftsatz_eigen: 'Eigene Schriftsätze an das Gericht – nur Kanzlei und Kunde.',
  schriftsatz_gegner: 'Schriftsätze der Gegenseite – nur Kanzlei und Kunde.',
  schriftsatz_streitverkuendeter: 'Schriftsätze von Streitverkündeten – nur Kanzlei und Kunde.',
  gericht_verfuegung: 'Verfügungen, Termine, Hinweise und sonstige Schreiben des Gerichts – nur Kanzlei und Kunde.',
  beweise: 'Lichtbilder der Unfallsituation, Kennzeichen, gegnerisches Fahrzeug und Zeugenfragebögen – nur Kanzlei und Kunde. Schadenfotos für das Gutachten gehören zu „Unterlagen Fahrzeugschaden gesamt“ bzw. „Schadenbelege“.',
  rechnungen: 'Sammelansicht aller Rechnungen der Akte. Erkannte Rechnungsarten liegen in ihrem Ordner (Gutachterrechnung → Gutachter / KVA, Reparaturrechnung → Autohaus / Werkstatt, Mietwagenrechnung → Mietwagen); nicht zugeordnete Rechnungen bleiben hier.',
  schadenbelege: 'Quittungen und Belege zu Schadenpositionen – Abschleppen, Taxi, Mietwagen, Kostenpauschale, Nutzungsausfall – nur Kanzlei und Kunde. Rechnungen liegen im Unterordner „Rechnungen“.',
  reparatur: 'Reparaturauftrag, Reparaturrechnung, Reparaturablaufplan und Notreparatur – für alle Beteiligten; der Materialbestellschein bleibt werkstattintern.',
  mietwagen: 'Mietvertrag, Mietwagenrechnung und Schriftwechsel mit der Autovermietung – Kanzlei, Kunde und sonstige Beteiligte (Vermieter).',
  abschlepper: 'Abschleppprotokoll und Unterlagen des Abschleppdienstes – Kanzlei, Kunde und sonstige Beteiligte (Abschleppdienst).',
  medizin: 'Ärztliche Berichte, Atteste und Befunde zum Personenschaden – nur Kanzlei und Kunde.',
  korrespondenz: 'Früher abgelegte E-Mails und Anhänge. E-Mails gehören heute in die Korrespondenz; der Ordner erscheint nur noch in Akten, die bereits Dokumente darin haben.',
  bank_leasing: 'Freigabeerklärung der Bank, Leasing- und Finanzierungsunterlagen, Sicherungsübereignung – nur Kanzlei und Kunde.',
  sonstiges: 'Alles, was in keinen anderen Ordner passt, sowie Originaldokumente aus der automatischen Auslese – für alle Beteiligten.',
}

/** Erklärung eines Standardordners (leer, wenn keine hinterlegt ist). */
export function classDescription (classId: string): string {
  return CLASS_DESCRIPTIONS[classId] ?? ''
}

/** Elternordner eines Unterordners (null = oberste Ebene). */
export function classParent (classId: string): string | null {
  return CLASS_PARENTS[classId] ?? null
}

/** Modus eines Standardordners; Unterordner erben den ihres Elternordners. */
export function classMode (classId: string): ClassMode {
  const id = classParent(classId) ?? classId
  if (AUTO_CLASS_IDS.has(id)) {
    return 'standard'
  }
  if (CONDITIONAL_CLASS_IDS.has(id)) {
    return 'bedingt'
  }
  return LEGACY_ONLY_CLASS_IDS.has(id) ? 'bestand' : 'vorlage'
}

/** Wird der Standardordner in jeder Akte automatisch angezeigt? */
export function isAutoClass (classId: string): boolean {
  return classMode(classId) === 'standard'
}

/** Falltyp-Bezug eines Standardordners. */
export function classScope (classId: string): ClassScope {
  return CLASS_SCOPES[classId] ?? 'alle'
}

/** Gilt ein Bereich (alle | haftpflicht | kasko) für den Falltyp der Akte? */
export function scopeAppliesTo (scope: string | null | undefined, caseType: string | null | undefined): boolean {
  const s = scope === 'haftpflicht' || scope === 'kasko' ? scope : 'alle'
  const t = caseType === 'kasko' ? 'kasko' : 'haftpflicht'
  return s === 'alle' || s === t
}

export interface ClassVisibilityInput {
  id: string
  /** Dokumente im Ordner (eigene Ablage). */
  count: number
  /** Eigener Ordner (Datenbankzeile) – immer sichtbar. */
  custom?: boolean
  /** Ein eigener Ordner hängt unter diesem Standardordner (parentClass). */
  hasCustomChildren?: boolean
  /** Ein Unterordner (Standard) enthält Dokumente. */
  hasFilledChildren?: boolean
}

export interface ClassShowContext {
  activated: ReadonlySet<string>
  permanent: ReadonlySet<string>
  /** Erfüllte Bedingungen je Klassen-ID (GET /doc-classes → conditions). */
  conditions?: Readonly<Record<string, boolean>>
}

/** Erscheint ein Ordner der obersten Ebene in der Akte? */
function isTopLevelShown (cls: ClassVisibilityInput, ctx: ClassShowContext): boolean {
  if (cls.custom || isAutoClass(cls.id)) {
    return true
  }
  if (CONDITIONAL_CLASS_IDS.has(cls.id) && ctx.conditions?.[cls.id]) {
    return true
  }
  return cls.count > 0 || !!cls.hasCustomChildren || !!cls.hasFilledChildren
    || ctx.activated.has(cls.id) || ctx.permanent.has(cls.id)
}

/**
 * Soll der Ordner in der Akte erscheinen? Grundbestand immer; bedingte
 * Ordner, wenn die Bedingung erfüllt ist; Vorlagen nur, wenn sie je Akte
 * oder dauerhaft aktiviert sind, Dokumente oder Unterordner enthalten.
 * Unterordner folgen ihrem Elternordner (parentShown).
 */
export function isClassShown (
  cls: ClassVisibilityInput,
  ctx: ClassShowContext,
  parentShown = false,
): boolean {
  if (classParent(cls.id)) {
    return parentShown || cls.count > 0
  }
  return isTopLevelShown(cls, ctx)
}

export interface PredefinedTemplate {
  id: string
  label: string
  description: string
  visibleTo: string[] | null
  scope: ClassScope
  /** Bereits in dieser Akte eingeblendet. */
  active: boolean
  /** Dauerhaft für alle Akten der Organisation aktiviert. */
  permanent: boolean
  /** Unterordner, die mit angelegt werden. */
  children: string[]
  /** Hinweis bei bedingten Ordnern („wird automatisch angelegt, wenn …"). */
  conditionHint: string
}

/**
 * Vordefinierte Vorlagen für den Dialog „Ordner anlegen" (Bereich a): alle
 * Standardordner außerhalb des Grundbestands, passend zum Falltyp – ohne
 * Unterordner (kommen mit dem Elternordner) und ohne Bestandsordner.
 */
export function predefinedTemplates (
  classes: Array<{ id: string, label: string, visibleTo?: string[] | null }>,
  caseType: string | null | undefined,
  activated: ReadonlySet<string>,
  permanent: ReadonlySet<string>,
): PredefinedTemplate[] {
  return classes
    .filter(c => !classParent(c.id) && ['vorlage', 'bedingt'].includes(classMode(c.id)) && scopeAppliesTo(classScope(c.id), caseType))
    .map(c => ({
      id: c.id,
      label: c.label,
      description: classDescription(c.id),
      visibleTo: c.visibleTo ?? null,
      scope: classScope(c.id),
      active: activated.has(c.id),
      permanent: permanent.has(c.id),
      children: classes.filter(child => classParent(child.id) === c.id).map(child => child.label),
      conditionHint: CONDITION_HINTS[c.id] ?? '',
    }))
}
